import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchCmsItemBySlug, getOptimizedImageUrl } from '../lib/cms'
import InquiryModal from '../components/InquiryModal'

export default function HotelDetailPage() {
  const { slug } = useParams()
  const [hotel, setHotel] = useState(null)
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    async function loadHotel() {
      setLoading(true)
      const data = await fetchCmsItemBySlug('hoteller', slug)
      if (data) {
        setHotel(data)
        document.title = `${data.hotelnavn} - SKI & CO TRAVEL ApS`
      }
      setLoading(false)
    }
    loadHotel()
  }, [slug])

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-400">
        <i className="fa-solid fa-spinner animate-spin text-3xl text-cyan-400 mb-3" aria-hidden="true" />
        <p>Henter hoteloplysninger...</p>
      </div>
    )
  }

  if (!hotel) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center space-y-6">
        <h1 className="font-heading font-extrabold text-3xl text-white">Hotel ikke fundet</h1>
        <Link to="/hoteller" className="inline-block px-6 py-3 rounded-xl bg-cyan-500 text-slate-950 font-bold">
          Tilbage til hoteller
        </Link>
      </div>
    )
  }

  const imageUrl = Array.isArray(hotel.hovedbillede)
    ? hotel.hovedbillede[0]
    : hotel.hovedbillede

  return (
    <div className="space-y-12 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <nav className="flex items-center gap-2 text-xs text-slate-400">
          <Link to="/" className="hover:text-cyan-400">Forside</Link>
          <span>/</span>
          <Link to="/hoteller" className="hover:text-cyan-400">Hoteller</Link>
          <span>/</span>
          <span className="text-slate-200 font-semibold">{hotel.hotelnavn}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative rounded-3xl overflow-hidden border border-slate-800 bg-slate-900 shadow-2xl">
          <div className="relative h-[360px] lg:h-[440px]">
            <img
              src={getOptimizedImageUrl(imageUrl, 1600, 85)}
              alt={hotel.hotelnavn}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />

            <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10 space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-cyan-400">
                {hotel.destination}
              </span>
              <h1 className="font-heading font-extrabold text-3xl sm:text-5xl text-white">
                {hotel.hotelnavn}
              </h1>
              {hotel.placering && (
                <p className="text-slate-200 text-sm flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-cyan-400" aria-hidden="true" />
                  <span>{hotel.placering}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="font-heading font-bold text-xl text-white">Beskrivelse</h2>
              {hotel.beskrivelse && (
                <div
                  className="prose prose-invert prose-cyan text-slate-300 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: hotel.beskrivelse }}
                />
              )}
            </div>

            {hotel.faciliteter && (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-3">
                <h3 className="font-heading font-bold text-lg text-white">Faciliteter</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{hotel.faciliteter}</p>
              </div>
            )}

            {hotel.vaerelsestyper && (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-3">
                <h3 className="font-heading font-bold text-lg text-white">Værelsestyper</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{hotel.vaerelsestyper}</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-4 space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-6 shadow-xl">
              <h3 className="font-heading font-bold text-xl text-white">Praktisk & Forplejning</h3>
              {hotel.maaltider && (
                <div className="text-sm text-slate-300 space-y-1">
                  <span className="text-slate-400 text-xs block font-semibold">Forplejning</span>
                  <p>{hotel.maaltider}</p>
                </div>
              )}

              <button
                onClick={() => setModalOpen(true)}
                className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-sm transition-colors"
              >
                Send forespørgsel med dette hotel
              </button>
            </div>
          </div>
        </div>
      </div>

      <InquiryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedDestination={hotel.destination}
      />
    </div>
  )
}
